/**
 * BprGraphSystemView - the D1 system-architecture map (layer 1 of the
 * knowledge graph): systems and integration points laid out left-to-right
 * with elk 'layered' on a React Flow canvas (same stack as BprDiagramCanvas,
 * via the shared flowLayout helper).
 *
 * Mapping from the render-agnostic diagram JSON (bprGraphDiagramSchema):
 * - kind 'system' nodes -> boxes with a findings badge and the system's logo
 *   (or initials), like the System Architecture boxes (diagramNodeIcon);
 * - kind 'integration' nodes -> dashed pills;
 * - edges WITH `via` collapse into a star through their integration pill
 *   (system -> pill -> system, deduped per endpoint pair) - the pill IS the
 *   edge label;
 * - edges WITHOUT `via` (co-manifestation seams) draw directly with their
 *   label. Gap edges are dashed red.
 *
 * Layout is deterministic: systems ordered by finding count (then id) feed
 * elk's model order, so the same diagram always draws the same picture.
 */

import '@xyflow/react/dist/style.css';

import type { BprGraphDiagram, BprGraphSystemViewNode } from '@protoss/shared';
import { Button } from '@protoss/ui';
import {
  Background,
  Handle,
  Panel,
  Position,
  ReactFlow,
  type Edge,
  type Node,
  type NodeProps,
} from '@xyflow/react';
import * as React from 'react';
import { diagramNodeIcon } from '../utils/diagramNodeIcon';
import { computeFlowCapture, downloadDiagramPdf } from '../utils/diagramPdf';
import { layoutElkGraph, type FlowLayoutLink } from '../utils/flowLayout';
import { DiagramNodeIconBadge } from './DiagramNodeIconBadge';

// Fixed footprints (elk layout inputs).
const SYSTEM_W = 200;
const SYSTEM_H = 64;
const PILL_W = 176;
const PILL_H = 48;

type SystemMapFlowNode = Node<
  { node: BprGraphSystemViewNode },
  'bprSystemMapNode' | 'bprSystemMapPill'
>;

function nodeTooltip(node: BprGraphSystemViewNode): string {
  return (
    `${node.label}${node.summary ? ` - ${node.summary}` : ''}\n` +
    `Findings: ${node.finding_counts.total} ` +
    `(critical ${node.finding_counts.Critical}, high ${node.finding_counts.High}, ` +
    `medium ${node.finding_counts.Medium}, low ${node.finding_counts.Low})`
  );
}

function FindingsBadge({ node }: { node: BprGraphSystemViewNode }) {
  if (node.finding_counts.total === 0) return null;
  return (
    <span className="bpr-system-map__badge">
      {node.finding_counts.total} finding{node.finding_counts.total === 1 ? '' : 's'}
    </span>
  );
}

function SystemMapBoxNode({ data }: NodeProps<SystemMapFlowNode>) {
  const node = data.node;
  // A system box's label is the system's name ("NetSuite ERP").
  const icon = diagramNodeIcon({ type: 'system', system: node.label }, 'system-architecture');
  return (
    <div
      className={`bpr-system-map__node bpr-system-map__node--with-icon${node.gap ? ' bpr-system-map__node--gap' : ''}`}
      title={nodeTooltip(node)}
    >
      <Handle type="target" position={Position.Left} className="bpr-system-map__handle" />
      {icon ? <DiagramNodeIconBadge icon={icon} /> : null}
      <div className="bpr-system-map__body">
        <div className="bpr-system-map__label">{node.label}</div>
        <FindingsBadge node={node} />
      </div>
      <Handle type="source" position={Position.Right} className="bpr-system-map__handle" />
    </div>
  );
}

function SystemMapPillNode({ data }: NodeProps<SystemMapFlowNode>) {
  const node = data.node;
  return (
    <div
      className={`bpr-system-map__pill${node.gap ? ' bpr-system-map__pill--gap' : ''}`}
      title={nodeTooltip(node)}
    >
      <Handle type="target" position={Position.Left} className="bpr-system-map__handle" />
      <div className="bpr-system-map__pill-label">{node.label}</div>
      <FindingsBadge node={node} />
      <Handle type="source" position={Position.Right} className="bpr-system-map__handle" />
    </div>
  );
}

const NODE_TYPES = { bprSystemMapNode: SystemMapBoxNode, bprSystemMapPill: SystemMapPillNode };

interface SystemMapFlow {
  nodes: Array<Omit<SystemMapFlowNode, 'position'>>;
  edges: Edge[];
  links: FlowLayoutLink[];
}

/**
 * Pure mapping from system_view JSON to React Flow nodes/edges (positions
 * come later from elk). Exported for testability.
 */
export function mapSystemViewToFlow(systemView: BprGraphDiagram['system_view']): SystemMapFlow {
  // Systems first (finding-count order = hub first), pills after: this order
  // feeds elk's considerModelOrder so the drawing is deterministic.
  const systems = systemView.nodes
    .filter((n) => n.kind === 'system')
    .sort((a, b) => b.finding_counts.total - a.finding_counts.total || a.id.localeCompare(b.id));
  const pills = systemView.nodes
    .filter((n) => n.kind === 'integration')
    .sort((a, b) => a.id.localeCompare(b.id));

  const nodes: SystemMapFlow['nodes'] = [
    ...systems.map((node) => ({
      id: node.id,
      type: 'bprSystemMapNode' as const,
      data: { node },
      width: SYSTEM_W,
      height: SYSTEM_H,
    })),
    ...pills.map((node) => ({
      id: node.id,
      type: 'bprSystemMapPill' as const,
      data: { node },
      width: PILL_W,
      height: PILL_H,
    })),
  ];
  const known = new Set(nodes.map((n) => n.id));

  // Collapse via-edges into a star through the integration pill: one segment
  // per (system, pill) pair, gap if any contributing edge is a gap.
  const segments = new Map<string, { source: string; target: string; gap: boolean }>();
  const addSegment = (source: string, target: string, gap: boolean) => {
    const key = [source, target].sort().join('|');
    const existing = segments.get(key);
    if (existing) existing.gap = existing.gap || gap;
    else segments.set(key, { source, target, gap });
  };

  const edges: Edge[] = [];
  systemView.edges.forEach((edge, idx) => {
    if (edge.via && known.has(edge.via)) {
      addSegment(edge.from, edge.via, edge.gap);
      addSegment(edge.via, edge.to, edge.gap);
      return;
    }
    // Co-manifestation seam (or a via pointing at an unknown node): direct
    // labeled edge between the systems.
    edges.push({
      id: `seam-${idx}`,
      source: edge.from,
      target: edge.to,
      label: edge.label,
      type: 'smoothstep',
      labelBgPadding: [6, 3] as [number, number],
      labelBgBorderRadius: 4,
      className: [
        'bpr-system-map__edge',
        'bpr-system-map__edge--seam',
        edge.gap ? 'bpr-system-map__edge--gap' : '',
      ]
        .filter(Boolean)
        .join(' '),
    });
  });
  for (const [key, segment] of segments) {
    edges.push({
      id: `via-${key}`,
      source: segment.source,
      target: segment.target,
      type: 'smoothstep',
      className: ['bpr-system-map__edge', segment.gap ? 'bpr-system-map__edge--gap' : '']
        .filter(Boolean)
        .join(' '),
    });
  }

  const links: FlowLayoutLink[] = edges
    .filter((e) => known.has(e.source) && known.has(e.target))
    .map((e) => ({ from: e.source, to: e.target }));

  return { nodes, edges, links };
}

export interface BprGraphSystemViewProps {
  systemView: BprGraphDiagram['system_view'];
  /** Title for the PDF download button; omit to hide the affordance. */
  exportTitle?: string;
}

export function BprGraphSystemView({ systemView, exportTitle }: BprGraphSystemViewProps) {
  const [nodes, setNodes] = React.useState<SystemMapFlowNode[]>([]);
  const [edges, setEdges] = React.useState<Edge[]>([]);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = React.useState(false);

  const handleExportPdf = React.useCallback(async () => {
    if (!containerRef.current || !exportTitle || isExporting || nodes.length === 0) return;
    setIsExporting(true);
    try {
      const capture = computeFlowCapture(containerRef.current, nodes);
      await downloadDiagramPdf(containerRef.current, exportTitle, capture, {
        // The system map's own vocabulary (not the BPR status legend).
        legend: [
          { label: 'system', swatch: 'node', color: '#64748B', fill: '#FFFFFF' },
          { label: 'integration point', swatch: 'line', color: '#64748B' },
          { label: 'integration gap', swatch: 'line', color: '#DC2626', dash: [3, 2] },
        ],
      });
    } catch {
      // Best-effort: a failed capture leaves the canvas untouched.
    } finally {
      setIsExporting(false);
    }
  }, [exportTitle, isExporting, nodes]);

  React.useEffect(() => {
    let cancelled = false;
    const flow = mapSystemViewToFlow(systemView);
    void layoutElkGraph(
      flow.nodes.map((n) => ({
        id: n.id,
        width: n.width ?? SYSTEM_W,
        height: n.height ?? SYSTEM_H,
      })),
      flow.links,
      'RIGHT'
    ).then((positions) => {
      if (cancelled) return;
      setNodes(
        flow.nodes.map((n) => ({
          ...n,
          position: positions.get(n.id) ?? { x: 0, y: 0 },
        }))
      );
      setEdges(flow.edges);
    });
    return () => {
      cancelled = true;
    };
  }, [systemView]);

  if (systemView.nodes.length === 0) {
    return <p className="bpr-graph__empty">The graph has no layer-1 system nodes to draw yet.</p>;
  }

  return (
    <figure className="bpr-system-map">
      <div className="bpr-system-map__canvas" ref={containerRef}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={NODE_TYPES}
          aria-label="System architecture map: systems and integration points, gaps flagged"
          fitView
          fitViewOptions={{ padding: 0.12, maxZoom: 1 }}
          minZoom={0.2}
          nodesDraggable
          nodesConnectable={false}
          edgesFocusable={false}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={24} />
          {exportTitle ? (
            <Panel position="top-right">
              {/* data-pdf-exclude keeps the button itself out of the capture. */}
              <div data-pdf-exclude="true">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isExporting}
                  onClick={() => void handleExportPdf()}
                >
                  {isExporting ? 'Exporting…' : 'Download PDF'}
                </Button>
              </div>
            </Panel>
          ) : null}
        </ReactFlow>
      </div>
      <figcaption className="bpr-system-map__legend">
        <span className="bpr-system-map__legend-item">System (badge = findings located on it)</span>
        <span className="bpr-system-map__legend-item bpr-system-map__legend-item--integration">
          Integration point
        </span>
        <span className="bpr-system-map__legend-item bpr-system-map__legend-item--gap">
          Dashed red = integration gap
        </span>
      </figcaption>
    </figure>
  );
}
